import debounce from 'lodash/debounce';
import { watch, type ComputedRef, type Ref } from 'vue';
import { COMPOSER_DEBOUNCE } from '@/components/shipment/form/domain/constants';
import type { RoutePlan } from '@gpx-spa/geo-domain';
import type { useShipmentFormStore } from '@/stores/useShipmentFormStore';

type FormStore = ReturnType<typeof useShipmentFormStore>;

export function useShipmentFormAutoRoute(options: {
  routesHydrationReady: Ref<boolean> | ComputedRef<boolean>;
  canCallRoutingApi: Ref<boolean> | ComputedRef<boolean>;
  canCompute: Ref<boolean> | ComputedRef<boolean>;
  stopsRouteFingerprint: Ref<string | null> | ComputedRef<string | null>;
  serverHydratedRouteFingerprint: Ref<string | null>;
  routePlan: Ref<RoutePlan | null>;
  formStore: FormStore;
  computeRoute: () => Promise<void>;
}) {
  const debouncedAutoComputeRoute = debounce(async () => {
    if (!options.routesHydrationReady.value) return;
    if (!options.canCompute.value) return;
    await options.computeRoute();
  }, COMPOSER_DEBOUNCE.autoRouteMs);

  watch(options.stopsRouteFingerprint, (fp) => {
    if (!options.routesHydrationReady.value) {
      debouncedAutoComputeRoute.cancel();
      return;
    }
    if (options.formStore.formHydrationSuspended) {
      debouncedAutoComputeRoute.cancel();
      return;
    }
    const serverFp = options.serverHydratedRouteFingerprint.value;
    if (!options.canCallRoutingApi.value) {
      debouncedAutoComputeRoute.cancel();
      if (options.routePlan.value && serverFp == null) {
        options.formStore.resetFormState();
      }
      return;
    }
    if (fp == null) {
      debouncedAutoComputeRoute.cancel();
      options.formStore.resetFormState();
      return;
    }
    if (serverFp != null && fp !== serverFp) {
      options.formStore.clearServerHydratedRouteFingerprint();
    }
    const skipFp = options.serverHydratedRouteFingerprint.value;
    if (skipFp != null && fp === skipFp) {
      debouncedAutoComputeRoute.cancel();
      return;
    }
    debouncedAutoComputeRoute();
  });

  return { debouncedAutoComputeRoute };
}
